import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { rciApi } from "../api/rci";
import { paymentsApi } from "../api/payments";
import { isNativeIOS } from "../utils/platform";
import { usersApi } from "../api/users";
import { Wordmark } from "../components/Wordmark";

const SUGGESTIONS = [
  "My body has felt off this week. What's going on?",
  "What's shaping how I'm doing right now?",
  "What's worth paying attention to today?",
  "Something feels misaligned. Can you see it?",
];

const STYLES = `
  .chat-screen {
    font-family: 'DM Sans', sans-serif;
    background: ${P.bg};
    height: 100vh;
    color: ${P.text};
    max-width: 420px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50%       { opacity: 1; }
  }

  .message-bubble {
    max-width: 84%;
    padding: 13px 16px;
    border-radius: 18px;
    line-height: 1.65;
    font-size: 14px;
    font-weight: 300;
    animation: fadeUp 0.3s ease forwards;
  }
  .bubble-haven {
    background: ${P.surface};
    color: ${P.text};
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 10px rgba(28,24,20,0.07);
    align-self: flex-start;
  }
  .bubble-user {
    background: ${P.primary};
    color: ${P.bg};
    border-bottom-right-radius: 4px;
    align-self: flex-end;
    font-weight: 400;
  }
  .suggestion-pill {
    background: ${P.surface};
    border: 1px solid ${P.border};
    border-radius: 99px;
    padding: 8px 14px;
    font-size: 12.5px;
    font-weight: 400;
    color: ${P.textMid};
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
    flex-shrink: 0;
  }
  .suggestion-pill:hover {
    background: ${P.primarySoft};
    border-color: ${P.primaryMid};
    color: ${P.primary};
  }
  .chat-input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 300;
    color: ${P.text};
    resize: none;
    line-height: 1.5;
    max-height: 120px;
    padding: 0;
  }
  .chat-input::placeholder { color: ${P.textFaint}; font-style: italic; }
  .send-btn {
    width: 36px; height: 36px; border-radius: 50%;
    background: ${P.primary}; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: opacity 0.15s; flex-shrink: 0;
  }
  .send-btn:disabled { background: ${P.border}; cursor: default; }
  .send-btn:not(:disabled):hover { opacity: 0.8; }
  .typing-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: ${P.primaryMid};
    animation: pulse 1.2s ease infinite;
  }
  .typing-dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-dot:nth-child(3) { animation-delay: 0.4s; }

  .haven-md p { margin: 0 0 8px; }
  .haven-md p:last-child { margin-bottom: 0; }
  .haven-md strong { font-weight: 500; }
  .haven-md em { font-style: italic; }
  .haven-md ul, .haven-md ol { margin: 4px 0 8px 16px; padding: 0; }
  .haven-md li { margin-bottom: 2px; }
  .haven-md hr { border: none; border-top: 1px solid ${P.border}; margin: 10px 0; }
  .haven-md h1, .haven-md h2, .haven-md h3 { font-size: inherit; font-weight: 500; margin: 8px 0 4px; }
`;

function TypingIndicator() {
  return (
    <div style={{ display: "flex", gap: 4, padding: "14px 16px",
      background: P.surface, borderRadius: 18, borderBottomLeftRadius: 4,
      alignSelf: "flex-start", boxShadow: "0 1px 10px rgba(28,24,20,0.07)",
      animation: "fadeUp 0.3s ease forwards" }}>
      <div className="typing-dot" />
      <div className="typing-dot" />
      <div className="typing-dot" />
    </div>
  );
}

export default function Chat({ onBack, seedMessage = null, seedContext = null }) {
  const { user, token, patchUser } = useAuth();
  // AI data-sharing consent (Guidelines 5.1.1(i)/5.1.2(i)). This is the
  // backend-enforced users.ai_data_consent flag — not the old local
  // acknowledgment — so the gate here mirrors the server, which 403s
  // chat for unconsented users. Gating lives inside the chat itself so
  // every entry point (reveal pull-threads, Signal CTA, dashboard FAB,
  // deep links) is covered, including the seeded auto-send below.
  const aiConsented = user?.ai_data_consent === true;

  const enableAIConsent = async () => {
    // Optimistic patch; the backend fails closed, so a lost opt-in only
    // re-shows the sheet — it never sends data without consent.
    patchUser({ ai_data_consent: true });
    try {
      await usersApi.setAIConsent(true, token);
    } catch { /* reconciled by the next /auth/me refresh */ }
  };
  // When seeded from a reveal pull thread we skip the cold greeting and go
  // straight into the seeded exchange.
  const [messages, setMessages] = useState(
    seedMessage
      ? []
      : [{ id: 0, role: "haven", text: "What\u2019s on your mind?" }]
  );
  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(!seedMessage);
  const [isSafetyGated, setIsSafetyGated] = useState(false);
  const [resourceBannerDismissed, setResourceBannerDismissed] = useState(false);
  const [paywalled, setPaywalled] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  // The reveal context (scores + insight) rides along on every message so the
  // conversation stays grounded; `source` only rides the first (seed) message
  // so thread_tapped is emitted once.
  const convoContext = useRef(
    seedContext ? { ...seedContext } : null
  );
  // The server-assigned thread id. Sent back on every subsequent
  // message so the backend can load the prior turns from its own
  // storage rather than trusting a transcript we resend. Null on the
  // first message of a thread, and stays null if the server couldn't
  // persist — in which case chat behaves exactly as it did before,
  // one turn at a time.
  const conversationIdRef = useRef(null);
  const seededRef = useRef(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  /* Check coherence_status for SAFETY_GATED */
  useEffect(() => {
    if (!user) return;
    rciApi.snapshot(user.id, token).then(snap => {
      if (snap?.coherence_status === 'SAFETY_GATED') {
        setIsSafetyGated(true);
        setShowSuggestions(false);
        setMessages([
          { id: 0, role: "haven", text: "You can share whatever feels present for you. There\u2019s no agenda here." },
        ]);
      }
    }).catch(() => {});
  }, [user, token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text, isSeed = false) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading || paywalled || !aiConsented) return;
    setInput("");
    setShowSuggestions(false);
    setMessages(prev => [...prev, { id: Date.now(), role: "user", text: trimmed }]);
    setLoading(true);
    // Build context: scores/insight always; source only on the seed message.
    let ctx = convoContext.current ? { ...convoContext.current } : undefined;
    if (isSeed && ctx) { ctx = { ...ctx, source: "reveal_thread", thread_text: trimmed }; }
    try {
      const res = await rciApi.chat(
        { message: trimmed, context: ctx, conversation_id: conversationIdRef.current },
        token,
      );
      if (res?.conversation_id) conversationIdRef.current = res.conversation_id;
      setMessages(prev => [...prev, { id: Date.now() + 1, role: "haven", text: res.reply }]);
      // Free (post-cutoff) user just spent their one comped exchange → CTA.
      if (res && res.can_continue === false) setPaywalled(true);
    } catch (err) {
      // 402 = free exchange already used → show the paywall CTA, not an error.
      if (err && (err.status === 402 || String(err.message || "").includes("402"))) {
        setPaywalled(true);
      } else {
        setMessages(prev => [...prev, { id: Date.now() + 1, role: "haven", text: "Something went wrong. Please try again." }]);
      }
    } finally {
      setLoading(false);
    }
  };

  /* Auto-send the seeded pull-thread message once — deferred until the
     AI disclosure has been acknowledged, so no data reaches the API
     before consent. */
  useEffect(() => {
    if (seedMessage && !seededRef.current && user && aiConsented) {
      seededRef.current = true;
      sendMessage(seedMessage, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedMessage, user, aiConsented]);

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await paymentsApi.createCheckoutSession();
      if (res?.checkout_url) { window.location.href = res.checkout_url; }
    } catch {
      setCheckoutLoading(false);
    }
  };

  const clearChat = () => {
    // Clearing the transcript starts a new thread server-side too —
    // otherwise the next message would silently resume the history the
    // user just asked to put away.
    conversationIdRef.current = null;
    if (isSafetyGated) {
      setMessages([{ id: 0, role: "haven", text: "You can share whatever feels present for you. There\u2019s no agenda here." }]);
      setShowSuggestions(false);
    } else {
      setMessages([{ id: 0, role: "haven", text: "What\u2019s on your mind?" }]);
      setShowSuggestions(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="chat-screen">

        {/* One-time AI-processing disclosure — blocks all chat
            interaction (typed and seeded) until acknowledged. */}
        {!aiConsented && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="How Haiven's AI chat uses your data"
            style={{
              position: "fixed", inset: 0, zIndex: 1200,
              background: "rgba(28,24,20,0.35)",
              display: "flex", alignItems: "flex-end", justifyContent: "center",
            }}
          >
            <div style={{
              width: "100%", maxWidth: 420, background: P.surface,
              borderRadius: "22px 22px 0 0",
              padding: "26px 24px calc(24px + env(safe-area-inset-bottom))",
              boxShadow: "0 -8px 40px rgba(28,24,20,0.18)",
              animation: "fadeUp 0.25s ease",
            }}>
              <div style={{
                fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                color: P.primaryMid, fontWeight: 500, marginBottom: 12,
              }}>
                Before you chat
              </div>
              <p style={{ fontSize: 13.5, color: P.text, fontWeight: 300,
                lineHeight: 1.7, margin: "0 0 10px" }}>
                Haiven&rsquo;s chat is powered by Claude, an AI model from
                Anthropic. To ground the conversation in your week, your
                messages are processed together with your signal scores,
                check-in responses, and the patterns in your Body, Mind,
                and Behavior data.
              </p>
              <p style={{ fontSize: 13.5, color: P.textMid, fontWeight: 300,
                lineHeight: 1.7, margin: "0 0 18px" }}>
                This data is sent securely to Anthropic via the Claude API,
                is used only to generate your insights, and is never used to
                train AI models. Chatting requires AI data sharing to be on —
                you can turn it off anytime in Settings.
              </p>
              <button
                type="button"
                onClick={enableAIConsent}
                style={{
                  width: "100%", padding: 15, borderRadius: 14,
                  background: P.primary, color: P.bg, border: "none",
                  cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13.5, fontWeight: 500,
                }}
              >
                Turn on AI data sharing
              </button>
              <button
                type="button"
                onClick={onBack}
                style={{
                  width: "100%", marginTop: 6, padding: 12,
                  background: "none", border: "none", cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 12.5, color: P.textFaint, fontWeight: 300,
                }}
              >
                Not now
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ padding: "max(24px, calc(env(safe-area-inset-top) + 12px)) 24px 16px", borderBottom: `1px solid ${P.border}`,
          background: P.bg, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              color: P.textMute, fontSize: 12, padding: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={P.textMute} strokeWidth="1.8" strokeLinecap="round">
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
              Back
            </button>
            <div style={{ textAlign: "center" }}>
              <Wordmark style={{ marginBottom: 0 }} />
              <div style={{ fontSize: 11, color: P.textFaint, marginTop: 1 }}>
                {isSafetyGated ? "Here with you" : "Signal"}
              </div>
            </div>
            <button onClick={clearChat} style={{ background: "none", border: "none", cursor: "pointer",
              fontSize: 11, color: P.textFaint, padding: 0 }}>
              Clear
            </button>
          </div>
        </div>

        {/* Safety-gated banner */}
        {isSafetyGated && (
          <div style={{
            padding: "12px 24px",
            background: P.surfaceAlt,
            fontSize: 13,
            color: P.textMid,
            fontWeight: 300,
            lineHeight: 1.6,
            textAlign: "center",
            fontStyle: "italic",
            flexShrink: 0,
          }}>
            You can share whatever's on your mind. There's no agenda here.
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 18px",
          display: "flex", flexDirection: "column", gap: 10 }}>
          {messages.map((msg) => (
            <div key={msg.id} className={`message-bubble bubble-${msg.role}`}>
              {msg.role === "haven" && (
                <div style={{ fontSize: 9, color: P.primaryMid, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 6 }}>
                  Haiven
                </div>
              )}
              {msg.role === "haven" ? (
                <div className="haven-md">
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>
              ) : (
                <span style={{ whiteSpace: "pre-wrap" }}>{msg.text}</span>
              )}
            </div>
          ))}
          {loading && <TypingIndicator />}
          <div ref={bottomRef} />
        </div>

        {/* Suggestions — hidden when SAFETY_GATED */}
        {showSuggestions && !loading && !isSafetyGated && (
          <div style={{ padding: "0 0 12px", overflowX: "auto",
            flexShrink: 0, scrollbarWidth: "none" }}>
            <div style={{ display: "flex", gap: 8, padding: "0 18px", width: "max-content" }}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} className="suggestion-pill" onClick={() => sendMessage(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Resource banner — persistent when SAFETY_GATED, dismissible per session */}
        {isSafetyGated && !resourceBannerDismissed && (
          <div style={{
            padding: "10px 18px",
            background: P.surface,
            borderTop: `1px solid ${P.border}`,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}>
            <p style={{ flex: 1, fontSize: 11.5, color: P.textMid, fontWeight: 300, lineHeight: 1.5, margin: 0 }}>
              If you need immediate support: <strong style={{ fontWeight: 500 }}>Crisis Text Line</strong> — Text HOME to 741741 | <strong style={{ fontWeight: 500 }}>988 Lifeline</strong> — Call or text 988
            </p>
            <button
              onClick={() => setResourceBannerDismissed(true)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: P.textFaint, fontSize: 16, padding: "0 2px", flexShrink: 0, lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Input \u2014 or, once a free user spends their one comped exchange,
            the paywall CTA replaces it entirely. The iOS App Store binary
            may not render a purchase CTA or point at external checkout
            (Guideline 3.1.1), so it gets a neutral end-of-preview note. */}
        {paywalled ? (
          <div style={{ padding: "16px 18px 22px", background: P.bg,
            borderTop: `1px solid ${P.border}`, flexShrink: 0, textAlign: "center" }}>
            {isNativeIOS() ? (
              <p style={{ fontSize: 13.5, color: P.textMid, fontWeight: 300,
                lineHeight: 1.6, margin: "0 auto", maxWidth: 280 }}>
                That\u2019s the first thread. Your daily check-ins keep building
                your signal \u2014 Haiven will keep noticing.
              </p>
            ) : (
              <>
                <p style={{ fontSize: 13.5, color: P.textMid, fontWeight: 300,
                  lineHeight: 1.6, margin: "0 auto 12px", maxWidth: 280 }}>
                  That\u2019s the first thread. There\u2019s a lot more underneath it.
                </p>
                <button
                  onClick={startCheckout}
                  disabled={checkoutLoading}
                  style={{ background: P.primary, color: P.bg, border: "none",
                    borderRadius: 14, padding: "14px 28px", fontSize: 14, fontWeight: 500,
                    cursor: "pointer", width: "100%", maxWidth: 300, margin: "0 auto",
                    opacity: checkoutLoading ? 0.6 : 1 }}
                >
                  {checkoutLoading ? "Opening checkout\u2026" : "Keep going \u2014 become a founding member"}
                </button>
              </>
            )}
          </div>
        ) : (
        <div style={{ padding: "12px 18px 20px", background: P.bg,
          borderTop: `1px solid ${P.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10,
            background: P.surface, borderRadius: 20, padding: "10px 14px",
            boxShadow: "0 1px 10px rgba(28,24,20,0.07)" }}>
            <textarea ref={inputRef} className="chat-input" rows={1}
              placeholder={isSafetyGated ? "I'm here. Share what's on your mind." : "Whatever you\u2019re noticing\u2026"}
              value={input} onChange={handleInput} onKeyDown={handleKeyDown} />
            <button className="send-btn" disabled={!input.trim() || loading}
              onClick={() => sendMessage()}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke={P.bg} strokeWidth="2.2" strokeLinecap="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <div style={{ textAlign: "center", marginTop: 8,
            fontSize: 10, color: P.textFaint }}>
            This isn't medical advice. It's a reflection of what Haiven has noticed.
          </div>
        </div>
        )}

      </div>
    </>
  );
}
